/**
 * Debounce the given `func` in order to prevent successive calls with less that `limit` milliseconds between each other.
 * @param func {function} - The debounced function.
 * @param [limit] {number} - The limit delay between 2 prevented calls.
 * @param [leading] {boolean} - Whether the call made for a group of debounced calls should be done at the beginning of that group.
 * @returns {function}
 */
export function debounce(func, limit = 200, leading = false) {
  let runningTimeout;
  return (...args) => {
    const later = () => {
      runningTimeout = null;
      if (!leading) {
        func(...args);
      }
    };
    const callNow = leading && !runningTimeout;
    clearTimeout(runningTimeout);
    runningTimeout = setTimeout(later, limit);
    if (callNow) {
      func(...args);
    }
  };
}

/**
 * Throttle the given `func` in order to prevent any call within `limit` milliseconds after every call actually made.
 * @param func {function} - The throttled function.
 * @param [limit] {number} - The limit delay after which a new call to `func` is allowed.
 * @returns {function}
 */
export function throttle(func, limit = 200) {
  let isRunning = false;
  return (...args) => {
    if (!isRunning) {
      isRunning = true;
      func(...args);
      setTimeout(() => {
        isRunning = false;
      }, limit);
    }
  };
}
